/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Pressable } from '@atlaskit/primitives/compiled';
import { HoverCard } from '@atlaskit/smart-card/hover-card';
import { token } from '@atlaskit/tokens';

import type { NLPSearchResultSource } from '../../../../ai-answer-dialog/types';
import { SainAction } from '../../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { SAINFeatureControl } from '../../../../common/utils/sain-feature-control';
import { extractUrlHostname } from '../../../../extract-url-hostname';
import { useAIAnswerContext } from '../../../ai-answer-context';
import { isSourceProduct3P } from '../../../analytics-helpers';
import { onAIAnswerSourceClick } from '../../../on-ai-answer-source-click';

import { SAIN_CITATION_ATTRIBUTE } from './citation-selectors';

const styles = cssMap({
	root: {
		border: 'none',
		backgroundColor: 'transparent',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		'&:hover': {
			cursor: 'pointer',
		},
	},
});

const convertColorAppearance = (color: string) => {
	switch (color) {
		case 'blue':
			return 'information';
		case 'green':
			return 'success';
		case 'red':
			return 'danger';
		case 'yellow':
			return 'warning';
		case 'purple':
			return 'discovery';
		default:
			return 'neutral';
	}
};

export type StatusCitationProps = {
	localId: string;
	text: string;
	color?: string;
};

export const StatusCitation: ({ localId, text, color }: StatusCitationProps) => JSX.Element = ({
	localId,
	text,
	color = 'blue',
}: StatusCitationProps) => {
	const { commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();
	const sources = commonAttributes.sources;
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { source, idx } = useMemo(() => {
		if (!localId.includes('citation')) {
			return {};
		}

		const idx = parseInt(localId.split('-')?.[1]);
		return {
			source: sources[idx - 1],
			idx,
		};
	}, [localId, sources]);

	const handleClick = useCallback(
		(source: NLPSearchResultSource, idx: number, e?: React.MouseEvent) => {
			const isAuxClick = e?.button === 1;
			const isContextMenu = e?.button === 2;
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: idx,
					isCitation: true,
					isAuxClick,
					isContextMenu,
					hostname: extractUrlHostname(source.url),
					is3PSource: isSourceProduct3P(source.product),
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.SOURCE_CLICKED, commonAttributes });
			if (!isContextMenu) {
				window.open(source.url, '_blank', 'noopener, noreferrer');
			}
		},
		[commonAttributes, fireAnalyticsEvent, fireSAINUsageEvent],
	);

	if (!!source) {
		return (
			<HoverCard url={source.url}>
				<Pressable
					{...{ [SAIN_CITATION_ATTRIBUTE]: 'true' }}
					xcss={styles.root}
					onClick={(e) => {
						handleClick(source, idx, e);
					}}
					onAuxClick={(e) => {
						if (e.button !== 1) {
							return;
						}
						handleClick(source, idx, e);
					}}
					onContextMenu={(e) => {
						handleClick(source, idx, e);
					}}
				>
					<Lozenge appearance="information">{idx}</Lozenge>
				</Pressable>
			</HoverCard>
		);
	}

	// Standard lozenge for status nodes
	return <Lozenge appearance={convertColorAppearance(color)}>{text}</Lozenge>;
};
